import { Check, Plus } from 'lucide-react'
import { useRef, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { toast } from '@/lib/toast'
import { addLabel, listManagedLabels } from '../settings/api'
import { LabelChip, type LabelLike } from './LabelChip'

/**
 * The list of a popover that opens from inside a modal (the "Nieuwe organisatie" sheet):
 * the modal's scroll lock cancels wheel and touch events from outside its own DOM, and the
 * popover is portaled to <body>. Stopping them here keeps the list scrollable.
 */
const keepScrolling = (event: { stopPropagation: () => void }) => event.stopPropagation()

/**
 * Add or remove labels from a selection; an unknown name becomes a new label with
 * the next palette colour (design §4.8). The caller owns the selected list.
 */
export function LabelPicker({
  selected,
  onChange,
  label = 'Label toevoegen',
}: {
  selected: LabelLike[]
  onChange: (labels: LabelLike[]) => void
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<LabelLike[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [query, setQuery] = useState('')
  const creating = useRef(false)
  const trimmed = query.trim()
  const needle = trimmed.toLowerCase()
  const chosen = new Set(selected.map((item) => item.id))
  const exact = options?.some((option) => option.name.toLowerCase() === needle)
  // Names that contain the text, those that start with it first. cmdk does not filter
  // (`shouldFilter={false}`): its own scoring ranked "create" above a match for text from
  // the middle of a name, and hid the "create" row, with its group, when nothing matched.
  const starts = (name: string) => name.toLowerCase().startsWith(needle)
  const visible = (options ?? [])
    .filter((option) => option.name.toLowerCase().includes(needle))
    .sort((a, b) => Number(starts(b.name)) - Number(starts(a.name)))

  function load() {
    setLoadFailed(false)
    listManagedLabels()
      .then(setOptions)
      .catch((caught) => {
        // The list stays unknown (not empty): opening the picker again tries again, and
        // no typed name is offered as new while the existing names are not known.
        setLoadFailed(true)
        toast.error(caught instanceof Error ? caught.message : 'Labels laden mislukt.')
      })
  }

  function changeOpen(next: boolean) {
    setOpen(next)
    if (next && options === null) load()
  }

  function toggle(option: LabelLike) {
    onChange(
      chosen.has(option.id)
        ? selected.filter((item) => item.id !== option.id)
        : [...selected, option],
    )
  }

  async function create(name: string) {
    // A second activation while the first is on its way would post the same name twice.
    if (creating.current) return
    creating.current = true
    try {
      const created = await addLabel({ name })
      setOptions((current) => [...(current ?? []), created])
      onChange([...selected, created])
      setQuery('')
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Label aanmaken mislukt.')
    } finally {
      creating.current = false
    }
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex h-[22px] items-center gap-1 rounded-full border border-dashed border-border px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          <Plus className="size-3" aria-hidden />
          Label
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Zoek of maak een label…" />
          <CommandList onWheel={keepScrolling} onTouchMove={keepScrolling}>
            {options === null ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {loadFailed ? 'Kon de labels niet laden.' : 'Laden…'}
              </p>
            ) : (
              <CommandEmpty>Geen labels gevonden.</CommandEmpty>
            )}
            <CommandGroup>
              {visible.map((option) => (
                <CommandItem key={option.id} value={option.id} onSelect={() => toggle(option)}>
                  <LabelChip label={option} />
                  {chosen.has(option.id) ? (
                    <Check className="ml-auto size-4" aria-label="gekozen" />
                  ) : null}
                </CommandItem>
              ))}
              {trimmed && options !== null && !exact ? (
                <CommandItem value="__nieuw__" onSelect={() => void create(trimmed)}>
                  <Plus className="size-4" aria-hidden />
                  Nieuw label &lsquo;{trimmed}&rsquo; aanmaken
                </CommandItem>
              ) : null}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
